import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { nanoid } from 'nanoid';

import type { ComboBoxOptions } from '@interstate/components/ComboBox';
import type { InterstateOnChangeCallback, InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { TradeReconData, TradeReconValuation, UseTradeReconCostReturn } from '../types/tradeReconCostTypes';
import { updateTradeReconItems } from '../actions/tradeReconCostActionCreators';
import type { SingleTradeInItem, ReconItem } from '../types/tradeInTypes';

export interface UseTradeReconCostProps {
    tradeIns: SingleTradeInItem[];
}

export const useTradeReconCost = ({ tradeIns }: UseTradeReconCostProps): UseTradeReconCostReturn => {
    // Initialize state for each trade
    const dispatch = useDispatch();
    const tradeReconSettings = useSelector(dealerSelectors.getTradeReconSettings);

    const buildTradeData = useCallback(
        (trade: any): TradeReconData => {
            const tradeData: TradeReconData = {
                value: trade.tradeIn.value || 0,
                acv: trade.tradeIn.acv || 0,
                amountOwed: trade.tradeIn.amountOwed || 0,
                tradeReconditionItems:
                    trade.tradeIn.tradeReconditionDetails?.tradeReconditionItems?.map((item: ReconItem) => ({
                        id: `recon-${nanoid()}`,
                        reconditionName: item.reconditionName,
                        reconditionAmount: item.reconditionAmount
                    })) || [],
                tradeReconditionTotal: trade.tradeIn.tradeReconditionDetails?.tradeReconditionTotal || 0
            };

            if (!trade.tradeIn.tradeReconditionDetails?.tradeReconditionItems?.length) {
                tradeReconSettings.tradeReconItems?.forEach((settingItem) => {
                    if (settingItem.isDefault) {
                        tradeData.tradeReconditionItems.push({
                            id: settingItem._id || `recon-${nanoid()}`,
                            reconditionName: settingItem.key,
                            reconditionAmount: settingItem.value
                        });
                        tradeData.tradeReconditionTotal += settingItem.value;
                    }
                });
            }

            return tradeData;
        },
        [tradeReconSettings]
    );

    const [tradeData, setTradeData] = useState<Record<string, TradeReconData>>(() => {
        const initialData: Record<string, TradeReconData> = {};
        tradeIns?.forEach((trade: any, index: number) => {
            initialData[`trade-${index + 1}`] = buildTradeData(trade);
        });
        return initialData;
    });

    useEffect(() => {
        // If tradeIns change (e.g. new trade added), initialize data for new trades
        setTradeData((prev) => {
            let changed = false;
            const updatedData = { ...prev };
            tradeIns?.forEach((trade: any, index: number) => {
                const tradeKey = `trade-${index + 1}`;
                if (!updatedData[tradeKey]) {
                    updatedData[tradeKey] = buildTradeData(trade);
                    changed = true;
                } else {
                    const newValue = trade.tradeIn.value || 0;
                    const newAcv = trade.tradeIn.acv || 0;
                    const newAmountOwed = trade.tradeIn.amountOwed || 0;
                    if (
                        updatedData[tradeKey].value !== newValue ||
                        updatedData[tradeKey].acv !== newAcv ||
                        updatedData[tradeKey].amountOwed !== newAmountOwed
                    ) {
                        updatedData[tradeKey] = {
                            ...updatedData[tradeKey],
                            value: newValue,
                            acv: newAcv,
                            amountOwed: newAmountOwed
                        };
                        changed = true;
                    }
                }
            });
            return changed ? updatedData : prev;
        });
    }, [tradeIns, tradeReconSettings, buildTradeData]);

    // Sync tradeData changes to Redux store
    useEffect(() => {
        dispatch(updateTradeReconItems(tradeData));
    }, [tradeData, dispatch]);

    const handleValuationChange = useCallback(
        (tradeKey: string, field: TradeReconValuation): InterstateOnChangeCallback<string> =>
            (event) => {
                const value = event.target.value;
                setTradeData((prev) => ({
                    ...prev,
                    [tradeKey]: {
                        ...prev[tradeKey],
                        [field]: Number(value) || 0
                    }
                }));
            },
        []
    );

    const handleReconItemNameChange = useCallback(
        (tradeKey: string, itemId: string) => (event: InterstateOnChangeEvent<ComboBoxOptions>) => {
            const value = event.target.value;
            // Extract the string value from ComboBoxOptions array
            const selectedValue = Array.isArray(value) && value.length > 0 ? value[0].value : '';
            const selectedItem = tradeReconSettings.tradeReconItems?.find((item) => item.key === selectedValue);
            setTradeData((prev) => ({
                ...prev,
                [tradeKey]: {
                    ...prev[tradeKey],
                    tradeReconditionItems: prev[tradeKey].tradeReconditionItems.map((item) =>
                        item.id === itemId
                            ? {
                                  ...item,
                                  reconditionName: selectedItem?.key ?? selectedValue,
                                  reconditionAmount: selectedItem?.value ?? item.reconditionAmount
                              }
                            : item
                    )
                }
            }));
        },
        []
    );

    const handleReconItemAmountChange = useCallback(
        (tradeKey: string, itemId: string) => (event: InterstateOnChangeEvent<string>) => {
            const value = event.target.value;
            const numericValue = Number(value) || 0;

            setTradeData((prev) => {
                const { updatedItems, total } = prev[tradeKey].tradeReconditionItems.reduce(
                    (acc, item) => {
                        const updatedItem = item.id === itemId ? { ...item, reconditionAmount: numericValue } : item;

                        acc.updatedItems.push(updatedItem);
                        acc.total += updatedItem.reconditionAmount;

                        return acc;
                    },
                    { updatedItems: [] as (typeof prev)[typeof tradeKey]['tradeReconditionItems'], total: 0 }
                );

                return {
                    ...prev,
                    [tradeKey]: {
                        ...prev[tradeKey],
                        tradeReconditionItems: updatedItems,
                        tradeReconditionTotal: total
                    }
                };
            });
        },
        []
    );

    const handleAddReconItem = useCallback((tradeKey: string) => {
        setTradeData((prev) => {
            const currentItems = prev[tradeKey]?.tradeReconditionItems || [];
            if (currentItems.length >= 15) {
                return prev;
            }
            return {
                ...prev,
                [tradeKey]: {
                    ...prev[tradeKey],
                    tradeReconditionItems: [
                        ...currentItems,
                        {
                            id: `recon-${nanoid()}`,
                            reconditionName: '',
                            reconditionAmount: 0
                        }
                    ]
                }
            };
        });
    }, []);

    const handleRemoveReconItem = useCallback((tradeKey: string, itemId: string) => {
        setTradeData((prev) => {
            const { filteredItems, total } = prev[tradeKey].tradeReconditionItems.reduce(
                (acc, item) => {
                    if (item.id !== itemId) {
                        acc.filteredItems.push(item);
                        acc.total += item.reconditionAmount;
                    }
                    return acc;
                },
                { filteredItems: [] as (typeof prev)[typeof tradeKey]['tradeReconditionItems'], total: 0 }
            );

            return {
                ...prev,
                [tradeKey]: {
                    ...prev[tradeKey],
                    tradeReconditionItems: filteredItems,
                    tradeReconditionTotal: total
                }
            };
        });
    }, []);

    return {
        tradeData,
        reconItemOptions: (tradeReconSettings.tradeReconItems ?? []).map((item) => ({
            label: item.key,
            value: item.key
        })),
        handleValuationChange,
        handleReconItemNameChange,
        handleReconItemAmountChange,
        handleAddReconItem,
        handleRemoveReconItem
    };
};
